import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAuctionSocket } from "@/lib/useAuctionSocket";
import PlayerPhoto from "@/components/PlayerPhoto";
import { Trophy, Broadcast, HandArrowUp } from "@phosphor-icons/react";

export default function TeamOwnerDashboard() {
  const { user, refresh } = useAuth();
  const [team, setTeam] = useState(null);
  const [state, setState] = useState(null);
  const auctionId = user?.auction_id;

  const loadTeam = useCallback(async () => {
    if (!user?.team_id) return;
    try { const { data } = await api.get(`/teams/${user.team_id}`); setTeam(data); } catch (e) { /* noop */ }
  }, [user?.team_id]);
  const loadState = useCallback(async () => {
    if (!auctionId) return;
    try { const { data } = await api.get(`/auctions/${auctionId}/state`); setState(data); } catch (e) { /* noop */ }
  }, [auctionId]);

  useEffect(() => { loadTeam(); loadState(); }, [loadTeam, loadState]);

  useAuctionSocket(auctionId, useCallback((msg) => {
    if (["player_sold", "player_unsold", "bid_placed", "sale_cancelled"].includes(msg.type)) { loadTeam(); loadState(); refresh(); }
    if (msg.state) setState(msg.state);
  }, [loadTeam, loadState, refresh]));

  if (!user?.team_id) {
    return (
      <div className="border border-dashed border-gray-200 rounded p-10 text-center text-gray-500" data-testid="team-owner-no-team">
        No team assigned to your account. Contact your administrator.
      </div>
    );
  }
  if (!team) return <div className="label-mono py-24 text-center">Loading team…</div>;

  const spent = team.budget - team.remaining_budget;
  const pct = team.budget ? Math.round((spent / team.budget) * 100) : 0;
  const slotsLeft = team.max_players - (team.players?.length || 0);

  return (
    <div className="space-y-10" data-testid="team-owner-dashboard">
      <div className="bg-white border border-gray-200 rounded-md p-6 md:p-8 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(600px 300px at 100% 0%, ${team.color}, transparent 60%)` }} />
        <div className="relative flex flex-col md:flex-row md:items-center gap-6">
          <div className="w-20 h-20 rounded grid place-items-center flex-shrink-0" style={{ background: `${team.color}22`, border: `2px solid ${team.color}55` }}>
            <Trophy weight="fill" size={36} style={{ color: team.color }} />
          </div>
          <div className="flex-1">
            <div className="label-mono">MY TEAM · {user.auction?.name}</div>
            <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">{team.name}</h1>
            <div className="label-mono mt-1">{team.short_name} · Owner {team.owner_name}</div>
          </div>
          <Link to={`/auction/${auctionId}/live`} className="btn-kinetic flex items-center justify-center gap-2" data-testid="go-live-auction">
            <Broadcast weight="fill" size={16} /> Live Auction
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded p-5" data-testid="team-budget-display">
          <div className="label-mono">Remaining Budget</div>
          <div className="stat-number text-emerald-600">{team.remaining_budget}</div>
          <div className="label-mono">pts</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-5">
          <div className="label-mono">Spent</div>
          <div className="stat-number">{spent}</div>
          <div className="h-1.5 bg-gray-100 rounded overflow-hidden mt-2"><div className="h-full" style={{ width: `${pct}%`, background: team.color }} /></div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-5">
          <div className="label-mono">Players Bought</div>
          <div className="stat-number">{team.players?.length || 0}</div>
          <div className="label-mono">of {team.max_players}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-5">
          <div className="label-mono">Slots Left</div>
          <div className="stat-number text-blue-600">{slotsLeft}</div>
          <div className="label-mono">Min {team.min_players}</div>
        </div>
      </div>

      {state?.current_player && (
        <div className="border border-emerald-500/40 bg-emerald-500/5 rounded p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="floor-hint">
          <div className="flex-1">
            <div className="label-mono flex items-center gap-2"><span className="live-dot" /> ON THE FLOOR</div>
            <div
              className="font-display text-3xl uppercase leading-tight text-white"
              style={{ textShadow: "0 2px 6px rgba(0,0,0,0.85), 0 0 3px rgba(0,0,0,0.9)" }}
            >{state.current_player.name}</div>
            <div className="label-mono">{state.current_player.role} · Base {state.current_player.base_price} · Current bid <span className="text-emerald-600">{state.current_bid}</span> pts</div>
          </div>
          <Link to={`/auction/${auctionId}/live`} className="btn-primary flex items-center gap-2" data-testid="jump-to-auction">
            <HandArrowUp weight="fill" size={16} /> Place Bid
          </Link>
        </div>
      )}

      <section data-testid="my-squad-list">
        <h2 className="font-display uppercase text-3xl mb-4">My Squad</h2>
        {(!team.players || team.players.length === 0) ? (
          <div className="border border-dashed border-gray-200 rounded p-10 text-center text-gray-500" data-testid="my-squad-empty">
            You haven't purchased any players yet.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {team.players.map((p) => (
              <div key={p.id} className="bg-white border border-gray-200 rounded overflow-hidden" data-testid={`my-player-${p.id}`}>
                <PlayerPhoto src={p.photo_url} alt={p.name} />
                <div className="p-3">
                  <div className="label-mono">{p.role}</div>
                  <div className="font-display text-lg leading-tight">{p.name}</div>
                  <div className="font-display text-2xl text-emerald-600 mt-1">{p.sold_price}<span className="text-xs text-gray-500 ml-1">pts</span></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
